import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Permission, StudentGpa, StudentGpaEditResource, StudentGpaViewResource } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StudentGpaPageBody } from "./StudentGpaPageBody";
import { StudentGpaPageHeader } from "./StudentGpaPageHeader";

const routeName = "studentGpa";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  studentId?: number = 0;
  schoolYear?: number = 0;
  schoolId?: number = 0;
  termTypeId?: number = 0;
  termNumber?: number = 0;
}

export const studentGpaPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "GPA",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

const ROOT_PERMISSION: Permission = Permission.StudentGpa;

export const StudentGpaPage = () => {
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const [gpa, setGpa] = useState<StudentGpa>({} as StudentGpa);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [gpa] });
  const [viewResource, setViewResource] = useState<StudentGpaViewResource>({});
  const [editResource, setEditResource] = useState<StudentGpaEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  const [studentId] = useState<number | undefined>(optionalParams.studentId);
  const [schoolYear] = useState<number | undefined>(optionalParams.schoolYear);
  const [schoolId] = useState<number | undefined>(optionalParams.schoolId);
  const [termTypeId] = useState<number | undefined>(optionalParams.termTypeId);
  const [termNumber] = useState<number | undefined>(optionalParams.termNumber);

  useEffect(() => {
    fetch(params.id);
    fetchViewResource();
  }, [params.id]);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${studentGpaPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`);

  const fetch = (id: number) => {
    ApiService.studentGpas.get(id).then((response) => {
      if (id === 0) {
        if (studentId) response.studentId = studentId;
        if (schoolYear) response.year = schoolYear;
        if (schoolId) response.schoolId = schoolId;
        if (termTypeId) response.termTypeId = termTypeId;
        if (termNumber) response.termNumber = termNumber;
      }

      setGpa(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = () => {
    ApiService.studentGpas.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.studentGpas.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const pageHeader = (
    <StudentGpaPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={studentGpaPageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetch}
      gpa={gpa}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <StudentGpaPageBody
      crudMode={params.crudMode}
      gpa={gpa}
      setGpa={setGpa}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      <Card>{pageBody}</Card>
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={pageHeader}
      content={pageBody}
    />
  );
};
